import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, RotateCcw, Info, TrendingUp, PieChart as PieChartIcon,
  LineChart as LineChartIcon, Table as TableIcon,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, ComposedChart, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  computeProjection, fmtCurrencyShort, fmtCurrencyExact, fmtPct, fmtMultiple,
  MAX_DURATION_YEARS,
} from '@/lib/wealthMath';
import { useAuth } from '@/contexts/AuthContext';

// -------- Wealth-management palette (scoped to this page only) --------
const INK = '#0F1A2B';        // deep navy — headings & primary line
const SLATE = '#4A5568';      // secondary text
const MUTE = '#94A3B8';       // muted gray — investment line
const BORDER = '#E2E8F0';     // subtle borders
const BG_ACCUM = '#F5F7FA';   // subtle background for accumulation phase
const BG_COMP = '#EEF2F7';    // slightly darker for compounding phase
const POS = '#2F855A';        // muted green for positive metrics
const GAIN_FILL = '#0F1A2B';
const SIP_FILL = '#4A5568';
const LUMP_FILL = '#94A3B8';

const DEFAULT_INPUT = {
  lumpSum: 100000,
  monthlySip: 2000,
  stepUpPct: 10,
  annualReturnPct: 12,
  durationYears: 30,
  sipStopYear: 20,
  yearsToWithdrawal: 10,
};

const PRESETS = {
  Conservative: { annualReturnPct: 8, stepUpPct: 5 },
  Balanced: { annualReturnPct: 11, stepUpPct: 10 },
  Growth: { annualReturnPct: 14, stepUpPct: 10 },
};

// -------- Reusable primitives ----------------------------------------------

const Label = ({ children, hint }) => (
  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-600 mb-2">
    <span>{children}</span>
    {hint && (
      <span className="group relative inline-flex" data-testid="field-tooltip">
        <Info className="w-3 h-3 text-slate-400" />
        <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-10 hidden group-hover:block w-64 rounded-sm border border-slate-200 bg-white p-3 text-xs normal-case tracking-normal text-slate-700 shadow-lg">
          {hint}
        </span>
      </span>
    )}
  </div>
);

const NumberField = ({ id, value, onChange, prefix, suffix, min = 0, max, step = 1, testId }) => (
  <div className="relative">
    {prefix && (
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{prefix}</span>
    )}
    <input
      id={id}
      type="number"
      value={Number.isFinite(value) ? value : ''}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      min={min}
      max={max}
      step={step}
      data-testid={testId}
      className={`w-full ${prefix ? 'pl-8' : 'pl-3'} ${suffix ? 'pr-10' : 'pr-3'} py-2.5 border border-slate-200 focus:border-slate-900 focus:outline-none text-slate-900 tabular-nums bg-white text-base rounded-sm transition-colors`}
    />
    {suffix && (
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{suffix}</span>
    )}
  </div>
);

const Slider = ({ id, value, onChange, min, max, step, testId }) => (
  <input
    id={id}
    type="range"
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    min={min}
    max={max}
    step={step}
    data-testid={testId}
    className="w-full accent-slate-900"
  />
);

const KpiCard = ({ label, value, sub, prominent = false, testId, positive = false }) => (
  <div
    data-testid={testId}
    className={`border border-slate-200 bg-white p-6 rounded-sm ${prominent ? 'md:col-span-2' : ''}`}
  >
    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div
      className={`mt-3 tabular-nums font-semibold ${prominent ? 'text-4xl lg:text-5xl' : 'text-2xl lg:text-3xl'} ${positive ? 'text-emerald-700' : 'text-slate-900'}`}
      style={{ letterSpacing: '-0.02em' }}
    >
      {value}
    </div>
    {sub && <div className="mt-1.5 text-xs text-slate-500">{sub}</div>}
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-4">{children}</div>
);

// -------- Custom tooltip for the line chart --------------------------------
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-md px-4 py-3 rounded-sm text-xs text-slate-700 min-w-[200px]">
      <div className="font-semibold text-slate-900 mb-1.5">Year {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6 py-0.5">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="tabular-nums font-medium text-slate-900">{fmtCurrencyExact(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------

export default function AdminWealthPlanner() {
  const { logout } = useAuth() || {};
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [activeTab, setActiveTab] = useState('chart'); // chart | table | breakdown
  const [activePreset, setActivePreset] = useState('Custom');

  const set = (patch) => {
    setInput((prev) => ({ ...prev, ...patch }));
    setActivePreset('Custom');
  };

  const applyPreset = (name) => {
    if (name === 'Custom') { setActivePreset('Custom'); return; }
    setInput((prev) => ({ ...prev, ...PRESETS[name] }));
    setActivePreset(name);
  };

  const reset = () => { setInput(DEFAULT_INPUT); setActivePreset('Custom'); };

  const projection = useMemo(() => computeProjection(input), [input]);
  const { annual, summary, notice } = projection;

  // Vertical marker positions on the chart
  const sipStopMarker = input.sipStopYear > 0 && input.sipStopYear <= annual.length ? input.sipStopYear : null;
  const withdrawalMarker = summary.effectiveEndYear;

  const insight = useMemo(() => {
    if (summary.growthAfterSipStop > 0 && input.sipStopYear > 0 && input.sipStopYear < summary.effectiveEndYear) {
      return `By stopping SIP contributions after Year ${input.sipStopYear} and allowing the portfolio to compound ` +
        `for another ${summary.compoundingOnlyYears} year${summary.compoundingOnlyYears === 1 ? '' : 's'}, ` +
        `approximately ${fmtCurrencyShort(summary.growthAfterSipStop)} of additional wealth is generated ` +
        `without any further contributions.`;
    }
    return `Investment growth represents approximately ${fmtPct(summary.gainSharePct, 0)} of your final projected portfolio.`;
  }, [summary, input.sipStopYear]);

  const breakdownData = [
    { name: 'Lump-Sum Investment', value: summary.lumpSum, fill: LUMP_FILL },
    { name: 'SIP Contributions', value: summary.totalSip, fill: SIP_FILL },
    { name: 'Investment Growth', value: Math.max(0, summary.totalGrowth), fill: GAIN_FILL },
  ].filter((d) => d.value > 0);

  return (
    <main className="min-h-screen bg-white text-slate-900" data-testid="wealth-planner-root" style={{ fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif' }}>
      {/* -------------- Header -------------- */}
      <div className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
            <div className="hidden md:block h-4 w-px bg-slate-200" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Private Wealth Planning Tools</div>
              <div className="font-semibold text-xl text-slate-900" style={{ letterSpacing: '-0.01em' }}>Wealth Projection Planner</div>
            </div>
          </div>
          <button onClick={logout} className="text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 transition-colors">
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <p className="text-sm text-slate-600 max-w-2xl mb-8">
          Model your investment contributions, step-up strategy, and long-term compounding potential across up to a hundred-year horizon.
        </p>

        {notice && (
          <div className="mb-6 border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 rounded-sm" data-testid="input-notice">
            {notice}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* -------------- LEFT: Inputs -------------- */}
          <aside className="lg:col-span-4 xl:col-span-4">
            <div className="border border-slate-200 rounded-sm bg-white">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <SectionTitle>Assumptions</SectionTitle>
                <button onClick={reset} data-testid="reset-btn" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 transition-colors">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
              </div>

              <div className="px-6 py-6 space-y-6">
                {/* Presets */}
                <div>
                  <Label>Preset Scenario</Label>
                  <div className="grid grid-cols-4 gap-1" role="tablist">
                    {['Conservative', 'Balanced', 'Growth', 'Custom'].map((p) => (
                      <button
                        key={p}
                        onClick={() => applyPreset(p)}
                        data-testid={`preset-${p.toLowerCase()}`}
                        className={`text-[11px] uppercase tracking-[0.14em] py-2 px-1 border transition-colors ${
                          activePreset === p ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600 hover:border-slate-400'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Lump-Sum Investment</Label>
                  <NumberField testId="input-lump-sum" prefix="$" value={input.lumpSum} onChange={(v) => set({ lumpSum: v })} min={0} step={1000} />
                </div>

                <div>
                  <Label>Monthly SIP</Label>
                  <NumberField testId="input-monthly-sip" prefix="$" value={input.monthlySip} onChange={(v) => set({ monthlySip: v })} min={0} step={100} />
                </div>

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label hint="The SIP amount steps up by this percentage at the start of every contribution year.">Annual SIP Step-Up</Label>
                    <span className="text-sm tabular-nums font-medium text-slate-900">{input.stepUpPct.toFixed(1)}%</span>
                  </div>
                  <Slider testId="input-step-up" value={input.stepUpPct} onChange={(v) => set({ stepUpPct: v })} min={0} max={100} step={0.5} />
                </div>

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label>Expected Annual Return</Label>
                    <span className="text-sm tabular-nums font-medium text-slate-900">{input.annualReturnPct.toFixed(2)}%</span>
                  </div>
                  <Slider testId="input-annual-return" value={input.annualReturnPct} onChange={(v) => set({ annualReturnPct: v })} min={0} max={100} step={0.25} />
                </div>

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label>Duration</Label>
                    <span className="text-sm tabular-nums font-medium text-slate-900">{input.durationYears} yr</span>
                  </div>
                  <Slider testId="input-duration" value={input.durationYears} onChange={(v) => set({ durationYears: v })} min={1} max={MAX_DURATION_YEARS} step={1} />
                </div>

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label hint="The last year during which monthly SIP contributions are made. After this year, your accumulated portfolio continues compounding without additional contributions.">SIP Stoppage Year</Label>
                    <span className="text-sm tabular-nums font-medium text-slate-900">Year {input.sipStopYear}</span>
                  </div>
                  <Slider testId="input-sip-stop" value={input.sipStopYear} onChange={(v) => set({ sipStopYear: v })} min={0} max={input.durationYears} step={1} />
                </div>

                <div>
                  <div className="flex items-baseline justify-between">
                    <Label hint="The number of years your portfolio remains invested after monthly SIP contributions stop.">Years to Withdrawal</Label>
                    <span className="text-sm tabular-nums font-medium text-slate-900">{input.yearsToWithdrawal} yr</span>
                  </div>
                  <Slider testId="input-years-to-withdrawal" value={input.yearsToWithdrawal} onChange={(v) => set({ yearsToWithdrawal: v })} min={0} max={MAX_DURATION_YEARS} step={1} />
                </div>
              </div>
            </div>
          </aside>

          {/* -------------- RIGHT: Results -------------- */}
          <section className="lg:col-span-8 xl:col-span-8 space-y-8">
            {/* KPI cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard
                prominent
                testId="kpi-final-value"
                label="Portfolio Value at Withdrawal"
                value={fmtCurrencyShort(summary.finalValue)}
                sub={`Projected value at end of Year ${summary.effectiveEndYear} · ${fmtCurrencyExact(summary.finalValue)}`}
              />
              <KpiCard
                testId="kpi-multiple"
                label="Wealth Multiple"
                value={fmtMultiple(summary.wealthMultiple)}
                sub="invested capital"
                positive={summary.wealthMultiple > 1}
              />
              <KpiCard
                testId="kpi-cagr"
                label="Effective CAGR"
                value={fmtPct(summary.cagr, 2)}
                sub={`on ${fmtCurrencyShort(summary.totalInvested)} invested`}
              />
              <KpiCard
                testId="kpi-total-invested"
                label="Total Amount Invested"
                value={fmtCurrencyShort(summary.totalInvested)}
                sub={`${fmtCurrencyShort(summary.lumpSum)} lump-sum · ${fmtCurrencyShort(summary.totalSip)} in SIPs`}
              />
              <KpiCard
                testId="kpi-total-gain"
                label="Total Investment Gain"
                value={fmtCurrencyShort(summary.totalGrowth)}
                sub={`${fmtPct(summary.gainSharePct, 0)} of final portfolio`}
                positive={summary.totalGrowth > 0}
              />
              <KpiCard
                testId="kpi-final-sip"
                label="Final Monthly SIP"
                value={fmtCurrencyShort(summary.finalSip)}
                sub={summary.finalSip > 0 ? `during Year ${input.sipStopYear}` : 'no SIP contributions'}
              />
            </div>

            {/* Post-SIP compounding analysis */}
            {summary.compoundingOnlyYears > 0 && (
              <div className="border border-slate-200 rounded-sm bg-slate-50/40" data-testid="post-sip-insight">
                <div className="px-6 py-5 border-b border-slate-200 flex items-baseline justify-between">
                  <SectionTitle>Post-SIP Compounding Analysis</SectionTitle>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
                    Years {input.sipStopYear + 1}–{summary.effectiveEndYear}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200">
                  <div className="bg-white px-6 py-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Value when SIP stops</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-slate-900">{fmtCurrencyShort(summary.valueAtSipStop)}</div>
                  </div>
                  <div className="bg-white px-6 py-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Value at withdrawal</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-slate-900">{fmtCurrencyShort(summary.valueAtWithdrawal)}</div>
                  </div>
                  <div className="bg-white px-6 py-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Wealth added after SIP</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-emerald-700">{fmtCurrencyShort(summary.growthAfterSipStop)}</div>
                  </div>
                  <div className="bg-white px-6 py-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Increase after SIP stop</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-emerald-700">{fmtPct(summary.pctIncreaseAfterSipStop, 0)}</div>
                  </div>
                </div>
                <div className="px-6 py-4 text-sm text-slate-700 border-t border-slate-200 leading-relaxed" data-testid="post-sip-narrative">
                  {insight}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="border border-slate-200 rounded-sm">
              <div className="flex border-b border-slate-200" role="tablist">
                {[
                  { key: 'chart', label: 'Growth Chart', icon: LineChartIcon },
                  { key: 'table', label: 'Annual Projection', icon: TableIcon },
                  { key: 'breakdown', label: 'Investment Breakdown', icon: PieChartIcon },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    data-testid={`tab-${key}`}
                    role="tab"
                    aria-selected={activeTab === key}
                    className={`flex-1 md:flex-none inline-flex items-center gap-2 px-6 py-4 text-[11px] uppercase tracking-[0.18em] transition-colors ${
                      activeTab === key ? 'text-slate-900 border-b-2 border-slate-900 -mb-px' : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {activeTab === 'chart' && (
                  <div style={{ width: '100%', height: 420 }} data-testid="growth-chart">
                    <ResponsiveContainer>
                      <ComposedChart data={annual} margin={{ top: 20, right: 24, left: 12, bottom: 0 }}>
                        <defs>
                          <linearGradient id="portfolio-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={INK} stopOpacity={0.08} />
                            <stop offset="100%" stopColor={INK} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={BORDER} vertical={false} />
                        <XAxis dataKey="year" tick={{ fill: SLATE, fontSize: 12 }} tickLine={false} axisLine={{ stroke: BORDER }} />
                        <YAxis
                          tick={{ fill: SLATE, fontSize: 12 }}
                          tickLine={false}
                          axisLine={{ stroke: BORDER }}
                          tickFormatter={(v) => fmtCurrencyShort(v)}
                          width={70}
                        />
                        {/* Phase backgrounds */}
                        {sipStopMarker && sipStopMarker < annual.length && (
                          <ReferenceLine x={sipStopMarker} stroke={INK} strokeDasharray="4 4" strokeOpacity={0.7} label={{ value: 'SIP Contributions End', position: 'top', fill: INK, fontSize: 11 }} />
                        )}
                        {withdrawalMarker && (
                          <ReferenceLine x={withdrawalMarker} stroke={POS} strokeDasharray="4 4" strokeOpacity={0.8} label={{ value: 'Planned Withdrawal', position: 'top', fill: POS, fontSize: 11 }} />
                        )}
                        <Tooltip content={<ChartTooltip />} />
                        <Legend verticalAlign="top" height={36} iconType="plainline" formatter={(v) => <span style={{ color: SLATE, fontSize: 12 }}>{v}</span>} />
                        <Area type="monotone" dataKey="endingBalance" name="Portfolio Value" stroke={INK} strokeWidth={2} fill="url(#portfolio-fill)" dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="totalInvested" name="Cumulative Invested" stroke={MUTE} strokeWidth={1.75} dot={false} isAnimationActive={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {activeTab === 'table' && (
                  <div className="overflow-x-auto -mx-6 -mb-6" data-testid="annual-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-y border-slate-200">
                        <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 text-right">
                          <th className="px-4 py-3 text-left">Year</th>
                          <th className="px-4 py-3 text-left">Phase</th>
                          <th className="px-4 py-3">Start</th>
                          <th className="px-4 py-3">Monthly SIP</th>
                          <th className="px-4 py-3">Step-Up</th>
                          <th className="px-4 py-3">Contributions</th>
                          <th className="px-4 py-3">Cumulative SIP</th>
                          <th className="px-4 py-3">Lump-Sum</th>
                          <th className="px-4 py-3">Total Invested</th>
                          <th className="px-4 py-3">Growth</th>
                          <th className="px-4 py-3">Ending Value</th>
                          <th className="px-4 py-3">Δ vs Start</th>
                        </tr>
                      </thead>
                      <tbody className="tabular-nums">
                        {annual.map((r) => (
                          <tr key={r.year} className={`border-b border-slate-100 hover:bg-slate-50/60 text-right ${r.phase === 'Planned Withdrawal' ? 'bg-emerald-50/40' : ''}`}>
                            <td className="px-4 py-2.5 text-left text-slate-900 font-medium">{r.year}</td>
                            <td className="px-4 py-2.5 text-left text-slate-600">{r.phase}</td>
                            <td className="px-4 py-2.5 text-slate-700">{fmtCurrencyShort(r.startingBalance)}</td>
                            <td className="px-4 py-2.5 text-slate-700">{fmtCurrencyShort(r.monthlySip)}</td>
                            <td className="px-4 py-2.5 text-slate-500">{r.stepUpPct ? `+${r.stepUpPct}%` : '—'}</td>
                            <td className="px-4 py-2.5 text-slate-700">{fmtCurrencyShort(r.annualContribution)}</td>
                            <td className="px-4 py-2.5 text-slate-700">{fmtCurrencyShort(r.cumulativeSip)}</td>
                            <td className="px-4 py-2.5 text-slate-500">{fmtCurrencyShort(r.lumpSum)}</td>
                            <td className="px-4 py-2.5 text-slate-700">{fmtCurrencyShort(r.totalInvested)}</td>
                            <td className="px-4 py-2.5 text-emerald-700">{fmtCurrencyShort(r.growthDuringYear)}</td>
                            <td className="px-4 py-2.5 text-slate-900 font-semibold">{fmtCurrencyShort(r.endingBalance)}</td>
                            <td className="px-4 py-2.5 text-slate-600">{fmtCurrencyShort(r.annualIncrease)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeTab === 'breakdown' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-testid="breakdown-tab">
                    <div style={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={breakdownData} dataKey="value" innerRadius={70} outerRadius={120} paddingAngle={1} isAnimationActive={false}>
                            {breakdownData.map((d, i) => <Cell key={i} fill={d.fill} stroke="#fff" strokeWidth={2} />)}
                          </Pie>
                          <Tooltip formatter={(v) => fmtCurrencyExact(v)} contentStyle={{ border: `1px solid ${BORDER}`, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-4">
                      <SectionTitle>Composition of Final Portfolio</SectionTitle>
                      {breakdownData.map((d) => {
                        const pct = summary.finalValue > 0 ? (d.value / summary.finalValue) * 100 : 0;
                        return (
                          <div key={d.name}>
                            <div className="flex items-baseline justify-between text-sm">
                              <span className="flex items-center gap-2 text-slate-700">
                                <span className="w-3 h-3" style={{ background: d.fill }} />
                                {d.name}
                              </span>
                              <span className="tabular-nums text-slate-900 font-medium">{fmtCurrencyShort(d.value)}</span>
                            </div>
                            <div className="h-1 bg-slate-100 mt-2 relative overflow-hidden">
                              <div className="absolute inset-y-0 left-0" style={{ background: d.fill, width: `${pct}%` }} />
                            </div>
                            <div className="mt-1 text-xs text-slate-500 tabular-nums">{fmtPct(pct, 1)} of final value</div>
                          </div>
                        );
                      })}
                      <div className="pt-4 mt-2 border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
                        Contributions account for {fmtPct(summary.contribSharePct, 0)} of your final portfolio; the remaining {fmtPct(summary.gainSharePct, 0)} is generated by compounding growth.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-[11px] leading-relaxed text-slate-500 pt-4" data-testid="disclaimer">
              Projections are hypothetical and for educational and planning purposes only. Actual investment returns may vary significantly and may include periods of loss. This calculator does not account for taxes, fees, inflation, market volatility, or changes in contribution behavior. It does not constitute investment, tax, or financial advice.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}
